<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store.js';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import PageLayout from 'dashboard/components-next/metaAgent/PageLayout.vue';
import StatusCard from 'dashboard/components-next/metaAgent/StatusCard.vue';
import SkillList from 'dashboard/components-next/metaAgent/SkillList.vue';
import UiSkillList from 'dashboard/components-next/metaAgent/UiSkillList.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const agent = useMapGetter('metaAgent/getAgent');
const skills = useMapGetter('metaAgent/getSkills');
const uiSkills = useMapGetter('metaAgent/getUiSkills');
const uiFlags = useMapGetter('metaAgent/getUIFlags');

const activeTab = ref(0);

const inboxId = computed(() => Number(route.params.inboxId));
const isOnboarded = computed(
  () => Boolean(agent.value.id) && agent.value.lifecycle !== 'pending_setup'
);

const tabs = computed(() => [
  {
    label: t('META_AGENT.SKILLS.TABS.INSTRUCTIONS'),
    count: skills.value.length,
  },
  {
    label: t('META_AGENT.SKILLS.TABS.RICH_MESSAGES'),
    count: uiSkills.value.length,
  },
]);

const alertFailure = error => {
  const data = error?.response?.data || {};
  useAlert(data.detail || data.error || t('META_AGENT.SKILLS.ERROR'));
};

// A failed load must say so rather than render as "no skills yet".
const load = async () => {
  try {
    await store.dispatch('metaAgent/getInboxes');
    await store.dispatch('metaAgent/getAgent', inboxId.value);
    if (isOnboarded.value) {
      await store.dispatch('metaAgent/getAllSkills', inboxId.value);
    }
  } catch (error) {
    alertFailure(error);
  }
};

// Returns whether the write landed, so the editor stays open on failure.
const run = async (action, payload, successKey) => {
  try {
    await store.dispatch(action, { inboxId: inboxId.value, ...payload });
    useAlert(t(successKey));
    return true;
  } catch (error) {
    alertFailure(error);
    return false;
  }
};

const saveSkill = payload =>
  run('metaAgent/saveSkill', payload, 'META_AGENT.SKILLS.INSTRUCTIONS.SUCCESS');

const deleteSkill = skillId =>
  run(
    'metaAgent/deleteSkill',
    { skillId },
    'META_AGENT.SKILLS.INSTRUCTIONS.DELETED'
  );

const saveUiSkill = payload =>
  run(
    'metaAgent/saveUiSkill',
    payload,
    'META_AGENT.SKILLS.RICH_MESSAGES.SUCCESS'
  );

const deleteUiSkill = uiSkillId =>
  run(
    'metaAgent/deleteUiSkill',
    { uiSkillId },
    'META_AGENT.SKILLS.RICH_MESSAGES.DELETED'
  );

onMounted(load);
watch(inboxId, load);
</script>

<template>
  <PageLayout
    :header-title="t('META_AGENT.SKILLS.HEADER')"
    :is-fetching="uiFlags.fetchingAgent || uiFlags.fetchingSkills"
  >
    <StatusCard
      v-if="!isOnboarded"
      variant="warning"
      :title="t('META_AGENT.OVERVIEW.ONBOARD.TITLE')"
      :description="t('META_AGENT.SKILLS.NOT_ONBOARDED')"
    />
    <div v-else class="flex flex-col gap-6">
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTab"
        @tab-changed="
          activeTab = tabs.findIndex(tab => tab.label === $event.label)
        "
      />
      <SkillList
        v-if="activeTab === 0"
        :save-handler="saveSkill"
        @delete="deleteSkill"
      />
      <UiSkillList v-else :save-handler="saveUiSkill" @delete="deleteUiSkill" />
    </div>
  </PageLayout>
</template>
